import React, { useState, useMemo, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AssetCard from './components/AssetCard';
import AssetPreviewModal from './components/AssetPreviewModal';
import AssetFilterBar from './components/AssetFilterBar';
import AssetCategoryTabs from './components/AssetCategoryTabs';
import Icon from '../../components/AppIcon';
import { assetService } from '../../services/assetService';

const AssetLibrary = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    orientation: '',
    campaign: '',
    search: ''
  });

  // Add this block - calculate category counts
  const categoryCounts = useMemo(() => {
    const counts = {};
    assets?.forEach((asset) => {
      const category = asset?.category || 'all';
      counts[category] = (counts?.[category] || 0) + 1;
    });
    return counts;
  }, [assets]);

  useEffect(() => {
    loadAssets();
  }, [activeCategory]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const data = await assetService?.getAll({ category: activeCategory });
      setAssets(data);
      setError('');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredAssets = useMemo(() => {
    return assets?.filter((asset) => {
      if (filters?.type && asset?.fileType !== filters?.type) return false;
      if (filters?.orientation && asset?.orientation !== filters?.orientation) return false;
      if (filters?.campaign && asset?.campaign !== filters?.campaign) return false;
      if (filters?.search && !asset?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
      return true;
    });
  }, [assets, filters]);

  const handleDownload = async (asset) => {
    try {
      const link = document.createElement('a');
      link.href = asset?.fullImage;
      link.download = asset?.title;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:ml-60">
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-sm">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Asset Library</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1 font-caption">
                Marketing assets ready for download and use
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!isAuthenticated && (
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
                >
                  <Icon name="Lock" size={16} />
                  Admin Login
                </button>
              )}
              <UserAccountMenu />
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6">
          <AssetCategoryTabs 
            activeCategory={activeCategory} 
            onCategoryChange={setActiveCategory} 
            categoryCounts={categoryCounts}
          />
          
          <div className="mt-6">
            <AssetFilterBar 
              filters={filters} 
              onFilterChange={setFilters}
              totalCount={assets?.length || 0}
              filteredCount={filteredAssets?.length || 0}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading assets...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <p className="text-destructive mb-4">{error}</p>
                <button
                  onClick={loadAssets}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : filteredAssets?.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Icon name="FolderOpen" size={64} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground font-medium text-lg mb-2">No assets found</p>
                <p className="text-muted-foreground">
                  {filters?.search || filters?.type || filters?.orientation || filters?.campaign
                    ? 'Try adjusting your filters' : 'No assets available in this category'}
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {filteredAssets?.map((asset) => (
                <AssetCard
                  key={asset?.id}
                  asset={asset}
                  onPreview={setSelectedAsset}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          )}
        </main>
      </div>
      {selectedAsset && (
        <AssetPreviewModal
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default AssetLibrary;